import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import { useWindowSize } from '../hooks/useWindowSize';
import NotificationBell from '../components/NotificationBell';
import ThemeToggle from '../components/ThemeToggle';
import UsersPage from './UsersPage';
import AccountsPage from './AccountsPage';
import TransfersPage from './TransfersPage';
import TransactionHistoryPage from './TransactionHistoryPage';
import NotificationsPage from './NotificationsPage';
import DebugPage from './DebugPage';

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const { tenant, tenantName } = useTenant();
  const { isDarkMode } = useTheme();
  const { width } = useWindowSize();
  const [currentPage, setCurrentPage] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadExchangeRates();
    
    // Listen for navigation events from NotificationBell
    const handleNotificationNavigation = () => {
      setCurrentPage('notifications');
    };
    
    window.addEventListener('navigate-to-notifications', handleNotificationNavigation);
    
    return () => {
      window.removeEventListener('navigate-to-notifications', handleNotificationNavigation);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDashboardSummary();
      setDashboardData(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadExchangeRates = async () => {
    try {
      const rates = await apiService.getExchangeRates();
      setExchangeRates(rates);
    } catch (error) {
      console.error('Failed to load exchange rates:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderContent = () => {
    switch (currentPage) {
      case 'users':
        return <UsersPage />;
      case 'accounts':
        return <AccountsPage />;
      case 'transfers':
        return <TransfersPage />;
      case 'transactions':
        return <TransactionHistoryPage />;
      case 'notifications':
        return <NotificationsPage />;
      case 'debug':
        return <DebugPage />;
      default:
        return (
          <div 
            data-testid="dashboard-overview"
            style={{ 
              padding: 'clamp(1rem, 3vw, 1.5rem)', 
              maxWidth: '1200px', 
              margin: '0 auto' 
            }}>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <h1 
                  data-testid="dashboard-greeting"
                  style={{ 
                    margin: 0, 
                    color: isDarkMode ? '#ffffff' : '#1a365d',
                    fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                    fontWeight: '600'
                  }}>
                  {getGreeting()}, {user?.firstName}!
                </h1>
                <div style={{
                  backgroundColor: isDarkMode ? '#1e3a2e' : '#e3f2fd',
                  color: isDarkMode ? '#51cf66' : '#1565c0',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}>
                  👋 {user?.role === 'ADMIN' ? 'Administrator' : 
                       user?.role === 'VIP_USER' ? 'VIP User' : 
                       user?.role === 'SUPPORT' ? 'Support' : 'User'}
                </div>
              </div>
              <p style={{ 
                margin: 0, 
                color: isDarkMode ? '#b3b3b3' : '#64748b',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
              }}>
                Welcome to your banking overview
              </p>
            </div>

            {error && (
              <div 
                data-testid="dashboard-error"
                style={{
                  backgroundColor: isDarkMode ? '#3a1e1e' : '#fef2f2',
                  color: isDarkMode ? '#ff6b6b' : '#dc2626',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  border: isDarkMode ? '1px solid #ff6b6b' : '1px solid #fecaca'
                }}>
                ⚠️ {error}
              </div>
            )}

            {loading ? (
              <div 
                data-testid="dashboard-loading"
                style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  height: '200px',
                  fontSize: '1.1rem',
                  color: isDarkMode ? '#b3b3b3' : '#64748b'
                }}>
                Loading your financial overview...
              </div>
            ) : (
              <div 
                data-testid="dashboard-content"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
                  gap: '1rem'
                }}>
                {/* Total Balance Card */}
                <div 
                  data-testid="total-balance-card"
                  style={{
                    backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.07)',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0',
                    gridColumn: 'span 2'
                  }}>
                  <h2 style={{ 
                    margin: '0 0 1rem 0', 
                    color: isDarkMode ? '#ffffff' : '#1a365d',
                    fontSize: '1.2rem',
                    fontWeight: '500'
                  }}>
                    💰 Total Balance
                  </h2>
                  <div 
                    data-testid="total-balance-amount"
                    style={{ 
                      fontSize: '2.5rem', 
                      fontWeight: '700',
                      color: '#059669',
                      marginBottom: '1rem'
                    }}>
                    {dashboardData ? formatCurrency(dashboardData.totalBalance) : '--'}
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: '2rem',
                    color: isDarkMode ? '#b3b3b3' : '#64748b',
                    fontSize: '0.9rem'
                  }}>
                    <div data-testid="checking-balance">
                      <strong>Checking:</strong> {dashboardData ? formatCurrency(dashboardData.accountsByType.checking) : '--'}
                    </div>
                    <div data-testid="savings-balance">
                      <strong>Savings:</strong> {dashboardData ? formatCurrency(dashboardData.accountsByType.savings) : '--'}
                    </div>
                    <div data-testid="accounts-count">
                      <strong>Accounts:</strong> {dashboardData?.accountsCount || 0}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div style={{
                  backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.07)',
                  border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0'
                }}>
                  <h3 style={{ 
                    margin: '0 0 1rem 0', 
                    color: isDarkMode ? '#ffffff' : '#1a365d',
                    fontSize: '1.1rem',
                    fontWeight: '500'
                  }}>
                    🚀 Quick Actions
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <button
                      onClick={() => setCurrentPage('transfers')}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                      💸 Send Money
                    </button>
                    <button
                      onClick={() => setCurrentPage('accounts')}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      🏦 Manage Accounts
                    </button>
                    <button
                      onClick={() => setCurrentPage('transactions')}
                      style={{
                        padding: '0.75rem',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#8b5cf6'}
                    >
                      📊 View History
                    </button>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div style={{
                  backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.07)',
                  border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0',
                  gridColumn: 'span 2'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <h3 style={{ 
                      margin: 0, 
                      color: isDarkMode ? '#ffffff' : '#1a365d',
                      fontSize: '1.1rem',
                      fontWeight: '500'
                    }}>
                      📈 Recent Transactions
                    </h3>
                    <button
                      onClick={() => setCurrentPage('transactions')}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: 'transparent',
                        color: isDarkMode ? '#4dabf7' : '#3b82f6',
                        border: isDarkMode ? '1px solid #4dabf7' : '1px solid #3b82f6',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}
                    >
                      View All
                    </button>
                  </div>
                  
                  {dashboardData?.recentTransactions?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {dashboardData.recentTransactions.map((transaction) => (
                        <div key={transaction.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          backgroundColor: isDarkMode ? '#1e1e1e' : '#f8fafc',
                          borderRadius: '6px'
                        }}>
                          <div>
                            <div style={{ 
                              fontWeight: '500',
                              color: isDarkMode ? '#ffffff' : '#1a365d',
                              marginBottom: '0.25rem'
                            }}>
                              {transaction.description}
                            </div>
                            <div style={{ 
                              fontSize: '0.8rem',
                              color: isDarkMode ? '#b3b3b3' : '#64748b'
                            }}>
                              {formatDate(transaction.date)}
                              {transaction.counterparty && ` • ${transaction.counterparty}`}
                            </div>
                          </div>
                          <div style={{
                            fontWeight: '600',
                            color: transaction.isIncoming ? '#059669' : '#dc2626'
                          }}>
                            {transaction.isIncoming ? '+' : '-'}{formatCurrency(transaction.amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: isDarkMode ? '#b3b3b3' : '#64748b',
                      padding: '2rem',
                      fontSize: '0.9rem'
                    }}>
                      No recent transactions
                    </div>
                  )}
                </div>

                {/* Exchange Rates */}
                <div style={{
                  backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: isDarkMode ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.07)',
                  border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0'
                }}>
                  <h3 style={{ 
                    margin: '0 0 1rem 0', 
                    color: isDarkMode ? '#ffffff' : '#1a365d',
                    fontSize: '1.1rem',
                    fontWeight: '500'
                  }}>
                    💱 Exchange Rates
                  </h3>
                  {exchangeRates ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {Object.entries(exchangeRates.rates).map(([currency, rate]) => (
                        <div key={currency} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '0.9rem'
                        }}>
                          <span style={{ color: isDarkMode ? '#ffffff' : '#1a365d', fontWeight: '500' }}>
                            1 USD = {currency}
                          </span>
                          <span style={{ color: isDarkMode ? '#b3b3b3' : '#64748b' }}>
                            {typeof rate === 'number' ? rate.toFixed(3) : rate}
                          </span>
                        </div>
                      ))}
                      <div style={{ 
                        fontSize: '0.75rem',
                        color: isDarkMode ? '#888888' : '#64748b',
                        marginTop: '0.5rem',
                        textAlign: 'center'
                      }}>
                        {exchangeRates.error ? exchangeRates.error : `Updated: ${exchangeRates.lastUpdated}`}
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      color: isDarkMode ? '#b3b3b3' : '#64748b',
                      fontSize: '0.9rem',
                      textAlign: 'center'
                    }}>
                      Loading rates...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  const navItems = [
    { id: 'overview', label: '🏠 Overview', roles: ['USER', 'VIP_USER', 'ADMIN', 'SUPPORT', 'SUPERUSER'] },
    { id: 'accounts', label: '💳 Accounts', roles: ['USER', 'VIP_USER', 'ADMIN', 'SUPPORT', 'SUPERUSER'] },
    { id: 'transfers', label: '💸 Transfers', roles: ['USER', 'VIP_USER', 'ADMIN', 'SUPPORT', 'SUPERUSER'] },
    { id: 'transactions', label: '📊 History', roles: ['USER', 'VIP_USER', 'ADMIN', 'SUPPORT', 'SUPERUSER'] },
    { id: 'notifications', label: '🔔 Notifications', roles: ['USER', 'VIP_USER', 'ADMIN', 'SUPPORT', 'SUPERUSER'] },
    { id: 'users', label: '👥 Users', roles: ['ADMIN', 'SUPPORT'] },
    { id: 'debug', label: '🐛 Debug', roles: ['SUPERUSER'] }
  ];

  const visibleNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: isDarkMode ? '#121212' : '#f1f5f9',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: isDarkMode ? '#ffffff' : '#1a365d'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: isDarkMode ? '#1a1a1a' : 'white',
        borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0',
        padding: '1rem',
        boxShadow: isDarkMode ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            minWidth: '0',
            flex: '1 1 auto'
          }}>
            <h1 style={{ 
              margin: 0, 
              color: isDarkMode ? '#ffffff' : '#1a365d',
              fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
              fontWeight: '700',
              whiteSpace: 'nowrap'
            }}>
              🏦 MiniBank
            </h1>
            <div style={{
              backgroundColor: isDarkMode ? '#2d2d2d' : '#f1f5f9',
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              fontSize: '0.7rem',
              color: isDarkMode ? '#b3b3b3' : '#64748b',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}>
              {tenantName}
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            flexShrink: 0
          }}>
            <ThemeToggle />
            <NotificationBell />
            <div style={{ 
              color: isDarkMode ? '#b3b3b3' : '#64748b', 
              fontSize: width > 768 ? '0.8rem' : '0.7rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <span style={{ display: width > 768 ? 'inline' : 'none' }}>
                {user?.firstName} {user?.lastName}
              </span>
              <span style={{ 
                display: width > 768 ? 'none' : 'inline',
                fontSize: '0.7rem',
                padding: '0.2rem 0.4rem',
                backgroundColor: isDarkMode ? '#2d2d2d' : '#f1f5f9',
                borderRadius: '4px',
                fontWeight: '500'
              }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            {width > 480 && (
              <div style={{
                backgroundColor: isDarkMode ? '#1e3a2e' : '#e0f2fe',
                color: isDarkMode ? '#51cf66' : '#0277bd',
                padding: '0.25rem 0.4rem',
                borderRadius: '4px',
                fontSize: '0.7rem',
                fontWeight: '500'
              }}>
                {user?.role}
              </div>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}
            >
              {width > 480 ? 'Logout' : '↗'}
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav style={{
        backgroundColor: isDarkMode ? '#1a1a1a' : 'white',
        borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0',
        padding: '0 1rem'
      }}>
        <div 
          className="horizontal-scroll"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            minWidth: 'max-content',
            padding: '0 1rem'
          }}>
            {visibleNavItems.map(item => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: currentPage === item.id 
                    ? (isDarkMode ? '#2d2d2d' : '#f1f5f9') 
                    : 'transparent',
                  color: currentPage === item.id 
                    ? (isDarkMode ? '#4dabf7' : '#3b82f6') 
                    : (isDarkMode ? '#b3b3b3' : '#64748b'),
                  border: 'none',
                  borderBottom: currentPage === item.id 
                    ? `2px solid ${isDarkMode ? '#4dabf7' : '#3b82f6'}` 
                    : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== item.id) {
                    e.target.style.backgroundColor = isDarkMode ? '#2d2d2d' : '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== item.id) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        {renderContent()}
      </main>
    </div>
  );
};

export default DashboardPage; 